import React, { useEffect, useState, Fragment } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import Accordion from "../Accordion";
import Loader from "components/Progress";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useGetAlarmsQuery } from "services/alarms";
import emitter from "Utilities/events";
import { useSelector } from "react-redux";

export default function Alarms(props) {
  let pageSize = 10;
  let token = window.localStorage.getItem("token");
  const [page, setPage] = useState(1);
  const device = useSelector((state) => state.asset.device);
  const sensors = device.esbMetaData && device.esbMetaData.datapoints && device.esbMetaData.datapoints.length ? props.sensors.filter(s=>device.esbMetaData.datapoints.includes(s.name)) : props.sensors;
  const sensorsFriendlyNames = sensors.map(s=>s.friendlyName)
  const [totalPages, setTotalPages] = useState(1);
  const fetchedAlarms = useGetAlarmsQuery({
    token,
    params: `?status=["ACTIVE","ACKNOWLEDGED"]&severity=${
      props.type
    }&pageSize=${pageSize}&withTotalPages=true&currentPage=${page}&source=${
      props.id || ""
    }&dateFrom=1970-01-01`,
  });
  const [alarms, setAlarms] = useState([]);
  const [count, setCount] = useState(0);

  // Returns an "effect" describing what changed, so the card-header
  // badge (total number of distinct ACTIVE alarm docs) only ticks when
  // an alarm was genuinely added or removed from the list.
  //
  // Note: a `replaced` effect happens on a coalescing re-fire — the
  // backend has bumped the existing alarm doc's per-alarm `count`
  // field, and we splice in the new payload which carries that bumped
  // count. The Accordion re-renders and the "Count: N" inside it goes
  // up correctly. What must NOT move in that case is the card-header
  // badge, since no new alarm doc was created. A previous version
  // incremented the badge on every `realtimeAction: "CREATE"`
  // regardless of whether the list actually grew, which drifted the
  // badge upward across every rule re-fire.
  function updateAlarm(payload) {
    let effect = "noop"; // "inserted" | "removed" | "replaced" | "noop"
    setAlarms((prev) => {
      let arr = [...prev];
      let index = arr.findIndex((e) => e.alarmId == payload.alarmId);
      if (index != -1) {
        if (payload.status == "CLEARED") {
          arr.splice(index, 1);
          effect = "removed";
        } else {
          arr.splice(index, 1, payload);
          effect = "replaced";
        }
      } else {
        arr.unshift(payload);
        effect = "inserted";
      }
      return arr;
    });
    return effect;
  }

  function updateCount(num) {
    setCount((prev) => prev + num);
  }

  useEffect(() => {
    function callbackfn(payload) {
      if (payload.message.severity !== props.type) return;
      const effect = updateAlarm(payload.message);
      if (effect === "inserted") updateCount(1);
      else if (effect === "removed") updateCount(-1);
      // `replaced` is a coalescing bump — the alarm was already in the
      // list under this alarmId, so the total count shouldn't move. A
      // previous version of this handler incremented on every CREATE
      // regardless, which drifted the badge upward across rule re-fires.
    }

    emitter.on("asset?alarms", callbackfn);

    return () => {
      emitter.off("asset?alarms", callbackfn);
    };
  }, [props.type]);

  function isJsonString(str) {
    let out;
    try {
      out = JSON.parse(str);
    } catch (e) {
      return str;
    }
    return out;
  }

  async function getAlarms() {
    if (!fetchedAlarms.isFetching && fetchedAlarms.isSuccess) {
      let res = JSON.parse(JSON.stringify(fetchedAlarms.data.payload.data));
      // res.forEach(r=>{
      //   if(isJsonString(r.text)){
      //     let friendlyName = JSON.parse(r.text).friendlyName;
      //     if(friendlyName){
      //       if(!sensorsFriendlyNames.includes(friendlyName)){
              
      //       }
      //     }
      //   }
      // })
      setCount(fetchedAlarms.data.payload.totalDocuments);
      setTotalPages(fetchedAlarms.data.payload.totalPages);
      setAlarms((prev) => {
        return [...prev, ...res];
      });
    }
  }

  function ifLoaded(state, component) {
    if (state)
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            height: "300px",
          }}
        >
          <Loader />
        </div>
      );
    else return component();
  }

  useEffect(() => {
    getAlarms();
  }, [fetchedAlarms.isFetching]);

  const handlePageNext = () => {
    setPage(page + 1);
  };
  const handlePagePrevious = () => {
    if (page > 1) setPage(page - 1);
  };

  function back(page) {
    let disabled;
    if (page > 1) disabled = false;
    else disabled = true;
    return (
      <IconButton
        size="medium"
        onClick={handlePagePrevious}
        disabled={disabled}
      >
        <NavigateBeforeIcon fontSize="inherit" />
      </IconButton>
    );
  }

  const clearAlarm = (id) => {
    const updatedAlarms = alarms.filter(alarm => alarm._id !== id);
    setCount(count - 1);
    setAlarms(updatedAlarms);
  };


  function alarmsFn() {
    return (
      <Fragment>
        <CardContent>
          {alarms.length == 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "150px",
                  width: "150px",
                }}
              >
                <NotificationsOffIcon
                  style={{ color: "#c7c7c7", fontSize: "100px" }}
                />
              </div>
              <p style={{ color: "#c8c8c8" }}>No alarms found</p>
            </div>
          ) : null}
          {alarms.map((alarm) => (
            <Accordion
              key={alarm._id || alarm.alarmId}
              alarm={alarm}
              permission={props.permission}
              alarmType={props.type}
              clear={(id) => clearAlarm(id)}
              alarmStatus={alarm.status}
            />
          ))}
        </CardContent>

        {page >= totalPages || alarms.length == 0 ? null : (
          <IconButton
            size="medium"
            onClick={handlePageNext}
            style={{ position: "relative", left: "50%", marginBottom: "15px" }}
          >
            <KeyboardArrowDownIcon fontSize="inherit" />
          </IconButton>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Card
        style={{
          minHeight: "300px",
          position: "relative",
          margin: "50px 20px 20px 0px",
          marginTop: "50px",
          overflow: "visible",
        }}
      >
        <span>
          <Card
            style={{
              width: "90px",
              height: "90px",
              backgroundColor: props.color,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "absolute",
              left: "20px",
              top: "0",
              transform: "translateY(-50%)",
            }}
          >
            <NotificationsActiveIcon
              style={{ color: "white", height: "35px", width: "35px" }}
            />
          </Card>
        </span>
        <span
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            position: "relative",
            right: "20px",
            top: "10px",
            paddingBottom: "30px",
          }}
        >
          <p
            style={{
              color: "#6d6d6d",
              fontWeight: "bold",
              fontSize: "13px",
              marginRight: "20px",
              marginTop: "3px",
            }}
          >
            {props.dashboardView != 1 ? props.type : props.label}
          </p>
          <h3>{count}</h3>
        </span>

        <div
          style={{
            height: "calc(50vh - 250px)",
            minHeight: "240px",
            overflow: "auto",
          }}
        >
          {ifLoaded(fetchedAlarms.isLoading, alarmsFn)}
        </div>
      </Card>
    </Fragment>
  );
}
